/// <amd-module name="TFS/Views/SuiteMePage/SuiteMePageView/ProjectsPivotService" />
import { IProjectData, ITeamProject, ITfsProjectService } from "TFS/Services/TfsProjectService";
import {
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    IReadyableReadonlyObservableArray,
    ObservableArray,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";

export interface IProjectsPivotView {
    /**
     * The most recently used projects for the current user
     */
    mruProjects: IReadonlyObservableArray<ITeamProject>;

    /**
     * All projects to display for the user
     */
    allProjects: IReadyableReadonlyObservableArray<ITeamProject>;

    /**
     * Whether or not there are any projects in this collection that the user has access to.
     * This is undefined until we know for sure whether there are any projects. We will know
     * this immediately if the user has any MRU projects. Otherwise, we won't know until after
     * a REST call finishes and tells us that there are no projects.
     */
    hasProjects: IReadonlyObservableValue<boolean | undefined>;

    /**
     * Query parameters related to project creation
     */
    projectCreateUrlParameters: IProjectCreateUrlParameters;

    /**
     * If we are in a first-run experience, this is the id of the pre-created project
     */
    preCreatedProjectId?: string;

    /**
     * If true, use the simplified version of the project creation form
     */
    useSimpleProjectCreate?: boolean;
}

export interface IProjectCreateUrlParameters {
    view?: string;
    _a?: string;
    acquisitionId?: string;
    action?: string;
    featureId?: string;
    product?: string;
    projectVisibility?: string;
    source?: string;
    versionControl?: string;
    processTemplate?: string;
}

/**
 * Service for getting data related to the projects overview pivot on the collection home page
 */
export interface IProjectsPivotViewSerivce extends IVssService {
    /**
     * Gets a view for the projects pivot with observable values that UI can subscribe to
     */
    getProjectsPivotView(): IProjectsPivotView;
}

class ProjectsPivotViewSerivce extends VssService implements IProjectsPivotViewSerivce {
    private view: IProjectsPivotView;

    public getProjectsPivotView(): IProjectsPivotView {
        if (!this.view) {
            const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            const initialUrlState = historyService.getState();

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

            let mruProjects: IReadonlyObservableArray<ITeamProject>;
            let allProjects: IReadyableReadonlyObservableArray<ITeamProject>;
            const hasProjects = new ObservableValue<boolean | undefined>(undefined);
            let preCreatedProjectId: string | undefined = undefined;
            const useSimpleProjectCreate = !!initialUrlState.product || !!initialUrlState.acquisition;

            // Check if we are in a first-run experience
            const firstRunData = contributionService.getData<{ preCreatedProjectId: string }>("ms.vss-tfs-web.collection-first-run-data-provider");
            if (firstRunData && firstRunData.preCreatedProjectId) {
                // First-run experience with a pre-created project. Use simple project creation
                mruProjects = new ObservableArray();
                allProjects = new ReadyableObservableArray();
                hasProjects.value = false;
                preCreatedProjectId = firstRunData.preCreatedProjectId;
            } else {
                const tfsProjectService = this.pageContext.getService<ITfsProjectService>("ITfsProjectService");

                // Get the user's current MRU projects as well as all projects in the collection
                mruProjects = tfsProjectService.getMruProjects();
                allProjects = tfsProjectService.getAllProjects();

                // hasProjects is tri-state. Set it to undefined until we have determined definitively
                // that there are no proejcts in the collection.
                if (mruProjects.length) {
                    // We have at least one MRU projects, so set hasProjects to true
                    hasProjects.value = true;
                } else if (allProjects.ready.value) {
                    // We have no MRU projects, but our all-projects data is already available. See if we have any projects now.
                    hasProjects.value = allProjects.value.length > 0;
                } else {
                    // We have no MRU projects and our all-projects data is being fetched asynchronously. Leave hasProjects as undefined until the data is available.
                    allProjects.ready.subscribe(() => {
                        hasProjects.value = this.view.allProjects.value.length > 0;
                    });
                }
            }

            this.view = {
                mruProjects,
                allProjects,
                hasProjects,
                preCreatedProjectId,
                useSimpleProjectCreate,
                projectCreateUrlParameters: initialUrlState as IProjectCreateUrlParameters
            };
        }

        return this.view;
    }
}

Services.add("IProjectsPivotViewSerivce", { serviceFactory: ProjectsPivotViewSerivce });
